import React, { useRef } from "react";
import ScrollModal from "../ui/ScrollModal.jsx";
import { Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";
import Tooltip from "./Tooltip";
import "./guides.css";
import guidesConfig from "../../config/guidesConfig";
import ch from './images/b365boost.webp';
import avsm from './images/av-priceboost-sm.png';
import avsb from './images/av-priceboost-sb.png';

import pbc from './images/priceboostcalc.gif';

// Assuming guidesConfig is defined and imported elsewhere in your project
const PriceBoostsGuide = () => {
  const meta = guidesConfig.priceBoostsGuide?.seo || {};
  const scrollRef = useRef(null);

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.priceBoostsGuide?.route || "/guides"}
      />

      <p>
        Price boosts (also known as <span className="key">odds boosts</span> or <span className="key">enhanced odds</span>) are one of the easiest and most reliable ways to make profits from matched betting.
      </p>
      <p>
        In this guide, we’ll explain exactly what price boosts are, how to spot profitable ones, how to back and lay them for guaranteed profits, and offer practical tips on where to lay your bets.
      </p>
      <p>Let’s get stuck in.</p>

      <h2>🔍 What Are Price Boosts?</h2>
      <p>
        A{" "}
        <Tooltip content="A price boost is a temporary increase in odds offered by bookmakers to attract more bets">
          price boost
        </Tooltip>{" "}
        is when a bookmaker temporarily increases the odds on a specific market to bring in more bets — especially before big sporting events. 
      </p>
      <img src={ch} alt="Bet365 super boost" />
      <p>For example, Bet365 boosted Constitution Hill to win at Cheltenham from 4/6 (1.667) to 6/4 (2.5).
      </p>
      <ul>
        <li>If Constitution Hill wins, a £10 bet returns £25.00 instead of £16.67.</li>
      </ul>
      <p>Remember: they’re counting on most people placing the boosted bet without thinking — you’re not most people.</p>
      <p>
        As  a matched bettor, price boosts are golden opportunities — if the boosted odds are higher than the lay odds available at an exchange, you can <strong>secure a profit</strong> no matter the outcome.
      </p>

      <h2>How to Spot a Profitable Price Boost</h2>
      <h6 className="info">If the <span className="back">back</span> odds are higher than the <span className="lay">lay</span> odds, there’s a profit to be made.</h6>
      <p>
        This is because when the bookmaker’s <span className="back">back</span> odds are higher than the exchange’s <span className="lay">lay</span> odds, the difference between them guarantees a profit, regardless of the event’s outcome.
      </p>
      <div className="example-container">
        <h4>Example</h4>
      <p><span className="key">Sky Bet</span> boost Aston Villa to win from <span className="key">1.57</span> to <span className="key">2.0</span>.</p>
      <p>The <span className="lay">lay</span> odds on <span className="key">Smarkets</span> are <span className="key">1.65</span>. We have a commission rate of 2%.</p>
      <p>Sky Bet will let us lay up to £20.</p>
        <div className="grid-fixed g2 mb-32">
            <div className="col">
                <img src={avsb} alt="Sky Bet price boost" />
            </div>
            <div className="col">
            <img src={avsm} alt="Smarkets lay profit boost" />
            </div>
        </div>
        <p>We use our <Link to="/calculators/lay-stake-calculator">price boost matched betting calculator</Link> to find our <span className="lay">lay</span> stake.</p>
        <p className="mb-32">If we <span className="back">back</span> Aston Villa using the boosted odds with a £20 stake, we could <span className="lay">lay</span> <span className="key">£24.54</span>. Our profit will be <span className="key">£4.05</span>, no matter the outcome.</p>
        <img src={pbc} alt="price boost matched betting calculator" />

      </div>

      <h2>How to Find Price Boosts</h2>
  <p>Price boosts are everywhere — if you know where to look.</p>

  <p>Most bookmakers promote their boosted odds clearly on their sites, especially in the lead-up to big events like televised football matches or major race days. You’ll often spot them on the homepage, in a dedicated <strong>“Boosts”</strong> or <strong>“Specials”</strong> tab, or highlighted during specific promotions.</p>

  <h3>Bookmakers That Regularly Offer Price Boosts</h3>
  <p>Some bookies are more generous — and more consistent — with price boosts than others. Keep a close eye on these:</p>

  <ul>
    <li>⭐ <strong>Bet365</strong> – Offers "Super Boosts" before major events often with fantastic odds.</li>
    <li>⭐ <strong>William Hill</strong> – Well-known for their daily football and horse racing boosts.</li>
    <li>⭐ <strong>Sky Bet</strong> – Offers frequent boosts before big football matches and includes enhanced “RequestABets” that can sometimes be layed.</li>
    <li>⭐ <strong>Paddy Power</strong> – Allows you to boost a market of your choice with “Power Ups”, as well as offering daily boosted markets.</li>
    <li>⭐ <strong>BetVictor, Ladbrokes, Coral, Betway</strong> – All have a regular stream of enhanced odds, especially for football and racing. Always double-check if there’s a matching lay market before jumping in.</li>
  </ul>

  <p>You can also follow your favourite bookies on <strong>social media</strong> or enable <strong>push/email notifications</strong> from their apps. Many send out exclusive price boosts as part of retention campaigns — often only available for a short time.</p>

      <h2>How to Back and Lay a Price Boost (Step-by-Step)</h2>
      <ol>
        <li>
          <strong>Find a Boosted Market</strong>
        </li>
        <li>
          <strong>Check the Exchange:</strong> Verify that you can lay the selection at <strong>lower odds</strong> on any exchange.
        </li>
        <li>
          <strong>Calculate Lay Stake:</strong> Input the following into your matched betting calculator:
          <ul>
            <li>Back odds</li>
            <li>Lay odds</li>
            <li>Back stake</li>
            <li>Commission</li>
          </ul>
          The calculator will suggest a lay stake and calculate the guaranteed profit. Check it's still profitable after commission.
        </li>
        <li>
          <strong>Place Your Bets</strong> 
        </li>
      </ol>


      <h2>Helpful Tools for Price Boosts</h2>
      <ul>
        <li>
          ✅ <strong>Matched Betting Calculator</strong> – Works out the exact lay stake for each boost.
        </li>
        <li>
          ✅ <strong>Price Boost Finders</strong> – <a href="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a> lists the most profitable boosts throughout the day.
        </li>
      </ul>
      <CTALarge
        to="/calculators/lay-stake-calculator"
        text="Go to Price Boosts Calculator"
        icon={Calculator}
      />

      <ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om2"}} />

      <h2>
        What If You Can’t Lay the Boost?
        
      </h2>
      <p>
        At times, some boosts are unlayable due to the absence of matching markets across exchanges like Betfair, Smarkets, Matchbook, or Betdaq. For instance:
      </p>
      <ul>
        <li>
          <strong>4/1 – Arsenal to win &amp; both teams to get 4+ corners</strong>
        </li>
        <li>
          <strong>9/1 – Salah to score a header &amp; Liverpool to win</strong>
        </li>
        <li>
          <strong>11/1 – Man City to win, over 10.5 corners &amp; a red card shown</strong>
        </li>
      </ul>
      <p>This means there's no guaranteed profit opportunity.</p>
      <p>
      Keep an eye on <a href="https://www.matchbook.com/events/enhanced-specials" target="_blank" rel="noreferrer">Matchbook</a> and <a href="https://www.betdaq.com/exchange/enhanced-specials-11251744" target="_blank" rel="noreferrer">Betdaq's ‘Enhanced Specials’</a> markets that often mirror the kinds of boosts bookies promote which could not otherwise be layed.

      </p>


      <p>If you think the boosted price is significantly higher than the true odds, it might still be worth a punt. This type of betting is known as <Tooltip content="Betting with advantage play is about long-term value, not guaranteeing a win on every bet">
          advantage play
        </Tooltip>{" "} (or AP for short).
        </p>
<h6 className="warn">Advantage play is an advanced strategy. You should be confident with the concept, and with basic matched betting, to attempt it.</h6>
<p>
With AP, you’re not locking in a guaranteed profit. You’re betting when the odds are in your favour. You won’t win every time, but if you pick your spots carefully, you can still beat the bookies in the long run.
</p>
<p>

The key with AP is managing your bankroll. Never over-stake or chase losses, and never gamble more than 1% of your bankroll.
</p>
<section>
  <h2>Is a Price Boost the Same as an Arb?</h2>
  <p>Not quite. While both price boosts and arbs (short for <strong>arbitrage bets</strong>) rely on a difference between back and lay odds, they’re not exactly the same thing.</p>

  <p><strong>Price boosts</strong> are promotional offers. Bookmakers <em>intentionally</em> raise the odds on a certain outcome to attract bets . These are designed to get attention and often come with a max stake, but they’re easy to spot and safe to use in matched betting.</p>

  <p><strong>Arbs</strong>, on the other hand, happen when bookmakers or exchanges disagree on the true odds of something. This creates a natural profit opportunity — you back at one place and lay at another, and no matter the outcome, you make money. But arbs usually appear because of <em>odds lag</em>, not promotions, and they’re often very short-lived.</p>

  <table>
    <thead>
      <tr>
        <th>Feature</th>
        <th>Price Boosts</th>
        <th>Arbs</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Intentional?</td>
        <td>✅ Yes – created by bookies</td>
        <td>❌ No – happen naturally</td>
      </tr>
      <tr>
        <td>Easy to find?</td>
        <td>✅ Often visible on homepage</td>
        <td>❌ Require software or fast spotting</td>
      </tr>
      <tr>
        <td>Risk of gubbing</td>
        <td>🔸 Low (if done smartly)</td>
        <td>🔴 High (bookies dislike arbing)</td>
      </tr>
      <tr>
        <td>Beginner friendly</td>
        <td>✅ Very</td>
        <td>⚠️ Not really</td>
      </tr>
    </tbody>
  </table>

  <p>In summary: <strong>All price boosts are potential arbs</strong> if the lay odds are low enough — but not all arbs are price boosts. For matched betting, price boosts are usually safer and easier to take advantage of without risking account restrictions.</p>
</section>


<h2>Advanced Strategy: Standard Lay vs Underlay</h2>
      <p>
        <strong>Standard Lay:</strong> This method secures the same profit regardless of the outcome, offering predictable returns.
      </p>
      <p>
        <strong>Underlay:</strong> With underlay, you lay slightly less than suggested by the calculator. This can yield a bigger profit if your back bet wins, but it might result in breaking even or a slight loss if it loses. This strategy is ideal when you’re confident in the outcome.
      </p>
      <h2>Will Price Boosts Get You Gubbed?</h2>
      <p>
        Bookmakers typically don’t mind if you take advantage of some price boosts. However, if you consistently extract value solely from boosts, your account might get flagged.
      </p>


      <h2>How Much Can You Make From Price Boosts?</h2>
      <p>
        While individual boosts may only yield modest profits — typically around £0.50–£3 — placing 3–5 boosts a day can accumulate to roughly £100–£300 per month with minimal risk.
      </p>

      <h2>Final Thoughts</h2>
      <p>
        Price boosts are a reliable and beginner-friendly strategy in matched betting. They’re simple to find, straightforward to execute, and offer consistent low-risk returns.
      </p>
      <p>
        Add price boosts to your betting strategy, and over time, those small profits will accumulate steadily.
      </p>
    </div>
  );
};

export default PriceBoostsGuide;
